import type { UserProfile } from "@/services/authService";
import {
  hasTenantPermission,
  isCustomFrontDeskRole,
  isSuperAdminRole,
  isTenantAdminRole,
} from "@/utils/accessControl";

export interface RoomCategoryAccess {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

/**
 * "constrained" roles (Tenant Admin, custom Front Desk) are limited by
 * rooms:read / rooms:administrate. Other roles (Hotel Admin, Front Desk
 * system role, Super Admin) keep full access.
 */
export type RoomCategoryRoleScope = "constrained" | "full";

export function getRoomCategoryRoleScope(
  userProfile: UserProfile | null,
): RoomCategoryRoleScope {
  if (isSuperAdminRole(userProfile)) {
    return "full";
  }
  return isTenantAdminRole(userProfile) || isCustomFrontDeskRole(userProfile)
    ? "constrained"
    : "full";
}

/**
 * Room category rights. View needs rooms:read or rooms:administrate; edit and
 * delete need rooms:administrate.
 */
export function getRoomCategoryAccess(
  hasPermission: (permission: string) => boolean,
  scope: RoomCategoryRoleScope = "constrained",
): RoomCategoryAccess {
  if (scope === "full") {
    return { canView: true, canEdit: true, canDelete: true };
  }
  const canAdminister = hasPermission("rooms:administrate");
  return {
    canView: canAdminister || hasPermission("rooms:read"),
    canEdit: canAdminister,
    canDelete: canAdminister,
  };
}

export function getRoomCategoryAccessForUser(
  userProfile: UserProfile | null,
): RoomCategoryAccess {
  return getRoomCategoryAccess(
    (permission) => hasTenantPermission(userProfile, permission),
    getRoomCategoryRoleScope(userProfile),
  );
}

export interface RoomCategoryFormState {
  nameReadOnly: boolean;
  descriptionReadOnly: boolean;
  priceReadOnly: boolean;
  formDisabled: boolean;
  canAddCustomFeature: boolean;
}

/**
 * Category card state. name, description and price come from the PMS for a
 * PMS hotel and are never editable here. Without the edit right the whole
 * form is inactive and Add Custom is hidden.
 */
export function getRoomCategoryFormState(
  access: RoomCategoryAccess,
  pmsEnabled: boolean,
): RoomCategoryFormState {
  const lockPmsFields = pmsEnabled || !access.canEdit;
  return {
    nameReadOnly: lockPmsFields,
    descriptionReadOnly: lockPmsFields,
    priceReadOnly: lockPmsFields,
    formDisabled: !access.canEdit,
    canAddCustomFeature: access.canEdit,
  };
}
